import { fromBackendChannel } from "../channel-map";
import { type ApiFetchContext, apiFetch } from "../client";
import type { BuildHeadersInput } from "../headers";
import {
  buildOrderDetailUrl,
  buildOrderListUrl,
  buildStoredOrderListUrl,
  type OrderListUrlInput,
  type StoredOrderListUrlInput,
} from "../path-adapter";
import type {
  BackendOrderStatus,
  CancelOrderItemParam,
  CancelOrderParam,
  CreateExpressOrderInput,
  OrderAddress,
  OrderDetail,
  OrderDetailRaw,
  OrderItem,
  OrderItemRaw,
  OrderListPageRaw,
  OrderListResult,
  OrderPayment,
  OrderSummary,
  OrderVoRaw,
  PaymentMethod,
  PayOrder4UserRaw,
  StorageExtendInput,
  StoredItem,
  StoredOrderListResult,
} from "../types/order";
import type { LangOpts } from "./product";

type OrderI18nOpts = LangOpts & Pick<BuildHeadersInput, "currency">;

const PAYMENT_METHODS = new Set<PaymentMethod>(["BALANCE", "GOLD_COIN", "THIRD", "OFFLINE"]);

/**
 * Normalize detailV2 `payOrder` into the receipt's payment leg. The backend
 * delivers `totalPayFee` / `calculateOrderAmount` in `currencyCode` WITHOUT
 * conversion, so we keep that currency and derive the handling fee from the
 * backend delta (never recompute the channel rate — combined payments only
 * charge the third-party slice, so the delta is the only correct fee).
 */
function normalizePayment(raw: PayOrder4UserRaw | undefined): OrderPayment | null {
  if (!raw) return null;
  const rawMethod = (raw.method ?? "").toUpperCase();
  const method: PaymentMethod = PAYMENT_METHODS.has(rawMethod as PaymentMethod)
    ? (rawMethod as PaymentMethod)
    : "UNKNOWN";
  const paid = raw.totalPayFee ?? 0;
  const base = raw.calculateOrderAmount ?? 0;
  const feeFloat = paid - base;
  return {
    payOrderId: raw.id,
    method,
    channelName: raw.channelName,
    currency: raw.currencyCode ?? "RUB",
    paidKopecks: Math.round(paid * 100),
    // Clamp tiny negative rounding / balance legs (no fee) to 0.
    feeKopecks: feeFloat > 0 ? Math.round(feeFloat * 100) : 0,
    payTime: raw.payTime,
  };
}

const PHOTO_CHECK_STATUSES = new Set<BackendOrderStatus>(["WAIT_PICKING", "WAIT_CONFIRM"]);

/**
 * Statuses for which the backend (`OrderController#cancelOrder`) allows
 * user self-service cancellation. Anything past WAIT_PROCESS (purchasing+)
 * must go through customer service. Mirror of the Java guard at
 * aps-api OrderController:2050 / :2094.
 */
export const SELF_CANCELLABLE_STATUSES = ["INIT", "WAIT_PAYMENT", "WAIT_PROCESS"] as const;

/** True when an order/item in this status can be self-cancelled by the user. */
export function isSelfCancellable(status: string): boolean {
  return (SELF_CANCELLABLE_STATUSES as readonly string[]).includes(status);
}

/**
 * Statuses for which the order still awaits the customer's payment, i.e. the
 * cashier (`/pay?orderNo=`) is reachable. WAIT_PAYMENT is the canonical one;
 * INIT is the pre-payment formal order. Anything past this has been paid (or
 * cancelled/expired) and must NOT surface a "去支付" / progress "paid" step.
 * Mirror of the backend pay-eligibility gate (aps-api PayController).
 */
export const PAYABLE_STATUSES = ["INIT", "WAIT_PAYMENT"] as const;

/** True when the order awaits payment and the cashier (/pay) is reachable. */
export function isPayable(status: string): boolean {
  return (PAYABLE_STATUSES as readonly string[]).includes(status);
}

function normalizeItem(raw: OrderItemRaw): OrderItem {
  return {
    id: raw.id,
    skuNo: raw.skuNo,
    spuNo: raw.spuNo,
    channel: fromBackendChannel(raw.channel ?? "TAOBAO"),
    title: raw.subject ?? "",
    imageUrl: raw.mainImg,
    quantity: raw.quantity ?? 1,
    priceRubKopecks: Math.round((raw.price ?? 0) * 100),
    totalPriceRubKopecks: raw.totalPrice != null ? Math.round(raw.totalPrice * 100) : undefined,
    originalPriceRubKopecks:
      raw.originalPrice != null && raw.originalPrice > 0
        ? Math.round(raw.originalPrice * 100)
        : undefined,
    valueServiceFeeRubKopecks:
      raw.additionalServiceFee != null && raw.additionalServiceFee > 0
        ? Math.round(raw.additionalServiceFee * 100)
        : undefined,
    skuAttrEnumFormatList:
      raw.skuAttrEnumFormatList && raw.skuAttrEnumFormatList.length > 0
        ? raw.skuAttrEnumFormatList
        : undefined,
    skuRemarks: raw.skuRemarks,
    shopCode: raw.shopCode,
    shopName: raw.shopName,
    shopLogoUrl: raw.logoUrl,
    status: raw.orderStatus ?? "INIT",
    statusName: raw.orderStatusName,
    checkPhotos: raw.checkPhotos ?? [],
    detailUrl: raw.detailUrl,
    expressNoList: (raw.expressNoList ?? []).filter((n) => typeof n === "string" && n.length > 0),
  };
}

function normalizeOrder(raw: OrderVoRaw): OrderSummary {
  const items = (raw.itemList ?? []).map(normalizeItem);
  const primaryChannel = items[0]?.channel ?? null;
  const hasPendingPhotoCheck = items.some((it) => PHOTO_CHECK_STATUSES.has(it.status));
  return {
    orderNo: raw.orderNo,
    status: raw.status,
    statusName: raw.statusName ?? raw.status,
    orderTime: raw.orderTime,
    payTime: raw.payTime,
    expiredTime: raw.expiredTime ?? null,
    cancelTime: raw.cancelTime,
    orderAmountRubKopecks: Math.round((raw.orderAmount ?? 0) * 100),
    itemAmountRubKopecks: Math.round((raw.itemAmount ?? 0) * 100),
    expressFeeRubKopecks: Math.round((raw.totalExpressFee ?? 0) * 100),
    itemCount: raw.itemQuantity ?? items.length,
    totalQuantity: raw.totalQuantity ?? items.reduce((acc, it) => acc + it.quantity, 0),
    primaryChannel,
    items,
    hasPendingPhotoCheck,
  };
}

/** One picked cart line to submit. Coupon is optional per line (matches the
 *  backend `ShopCart` shape used by /order/submitV2). */
export interface SubmitOrderCartItem {
  id: string;
  couponCustomerId?: string;
}

export interface SubmitOrderInput {
  /** User delivery address id (required by the backend — blank → 601). */
  addressId: string;
  /** The exact ticked cart-item ids (subset billing per #183). */
  cartItems: SubmitOrderCartItem[];
  /** Buyer note carried over from the confirm page. */
  remarks?: string;
}

/**
 * POST /order/submitV2 — turns the picked cart lines into a FORMAL order and
 * returns its real `orderNo`. This is the order number the cashier
 * (`POST /channel/list`) needs; the pre-order's `preOrderNo` is NOT a payable
 * order number. Mirrors the legacy uniapp confirm-order submit flow
 * (`{addressId, cartItems:[{id, couponCustomerId}]}` → redirect to pay).
 */
export async function submitOrder(
  ctx: ApiFetchContext,
  authToken: string,
  input: SubmitOrderInput,
): Promise<string> {
  return apiFetch<string>(ctx, "/order/submitV2", {
    method: "POST",
    authToken,
    body: {
      addressId: input.addressId,
      cartItems: input.cartItems.map((it) => ({
        id: it.id,
        ...(it.couponCustomerId ? { couponCustomerId: it.couponCustomerId } : {}),
      })),
      ...(input.remarks ? { remarks: input.remarks } : {}),
    },
  });
}

export async function queryOrderDetail(
  ctx: ApiFetchContext,
  authToken: string,
  orderNo: string,
  opts: OrderI18nOpts = {},
): Promise<OrderDetail> {
  const raw = await apiFetch<OrderDetailRaw>(ctx, buildOrderDetailUrl({ orderNo }), {
    authToken,
    ...opts,
  });
  const items = (raw.orderItemList ?? []).map(normalizeItem);
  const addr: OrderAddress | null = raw.orderAddress
    ? {
        receiverName: raw.orderAddress.recipientName ?? "",
        mobile: raw.orderAddress.mobile ?? "",
        country: raw.orderAddress.countryText ?? "",
        province: raw.orderAddress.provinceText ?? "",
        city: raw.orderAddress.cityText ?? "",
        district: raw.orderAddress.areaText ?? "",
        detail: raw.orderAddress.addressDetail ?? "",
        postCode: raw.orderAddress.postalCode ?? "",
      }
    : null;
  const o = raw.order;
  const valueServiceFeeRubKopecks = items.reduce(
    (acc, it) => acc + (it.valueServiceFeeRubKopecks ?? 0),
    0,
  );
  return {
    orderNo: o.orderNo,
    status: o.status,
    statusName: o.statusName ?? o.status,
    payTime: o.payTime,
    orderTime: o.orderTime,
    cancelTime: o.cancelTime,
    remarks: o.remarks,
    orderAmountRubKopecks: Math.round((o.orderAmount ?? 0) * 100),
    itemAmountRubKopecks: Math.round((o.itemAmount ?? 0) * 100),
    expressFeeRubKopecks: Math.round((o.totalExpressFee ?? 0) * 100),
    serviceFeeRubKopecks: Math.round((o.serviceFee ?? 0) * 100),
    valueServiceFeeRubKopecks,
    totalPayAmountRubKopecks: Math.round((o.totalPayAmount ?? 0) * 100),
    couponDiscountAmountRubKopecks: Math.round((o.couponDiscountAmount ?? 0) * 100),
    items,
    address: addr,
    payment: normalizePayment(raw.payOrder),
  };
}

export async function queryOrders(
  ctx: ApiFetchContext,
  authToken: string,
  input: OrderListUrlInput = {},
  opts: OrderI18nOpts = {},
): Promise<OrderListResult> {
  const page = await apiFetch<OrderListPageRaw>(ctx, buildOrderListUrl(input), {
    authToken,
    ...opts,
  });
  const records = (page.records ?? []).map(normalizeOrder);
  return {
    records,
    total: page.total ?? records.length,
    pageNo: page.current ?? input.pageNo ?? 1,
    pageSize: page.size ?? input.pageSize ?? 10,
    pages: page.pages ?? 1,
  };
}

/**
 * Flatten + dedupe the per-page `records` from a `useOrdersInfinite` result.
 *
 * Why dedupe is required: `GET /order/listV2` paginates over **order *items***
 * (rows of `order_item`), then collapses the items on each page back into their
 * distinct parent **orders** before returning (aps-boot `OrderController.listV2`).
 * Two consequences for the client (root cause of #442 — "8 orders, only 4 show"):
 *
 *  1. With `pageSize=10` *items*, a single item-page can collapse to far fewer
 *     orders (e.g. multi-item orders → 10 items ≈ 4 orders), so one page never
 *     shows every order.
 *  2. One order's items can straddle an item-page boundary, so the *same*
 *     `orderNo` appears on two consecutive item-pages. Naive concat would render
 *     a duplicate card.
 *
 * So the infinite list must fetch every item-page (until `getNextPageParam`
 * returns undefined) and merge their `records` keyed by `orderNo`, keeping the
 * first occurrence (backend already sorts orders newest-first). The result is
 * the complete, de-duplicated set of orders.
 */
export function mergeOrderPages(pages: readonly OrderListResult[]): OrderSummary[] {
  const seen = new Set<string>();
  const out: OrderSummary[] = [];
  for (const page of pages) {
    for (const order of page.records) {
      if (seen.has(order.orderNo)) continue;
      seen.add(order.orderNo);
      out.push(order);
    }
  }
  return out;
}

function normalizeStoredItem(raw: OrderItemRaw): StoredItem {
  const out: StoredItem = {
    id: raw.id,
    orderNo: raw.orderNo ?? "",
    channel: fromBackendChannel(raw.channel ?? "TAOBAO"),
    title: raw.subject ?? "",
    quantity: raw.quantity ?? 1,
    status: raw.orderStatus ?? "STORED",
    // OrderItemVo.price is already in the request currency — to minor units.
    priceRubKopecks: Math.round((raw.price ?? 0) * 100),
    checkPhotos: raw.checkPhotos ?? [],
  };
  if (raw.mainImg) out.imageUrl = raw.mainImg;
  if (raw.skuRemarks) out.skuRemarks = raw.skuRemarks;
  if (raw.shopName) out.shopName = raw.shopName;
  if (raw.logoUrl) out.shopLogoUrl = raw.logoUrl;
  if (raw.orderStatusName) out.statusName = raw.orderStatusName;
  if (raw.originalPriceCNY != null && raw.originalPriceCNY > 0)
    out.originalPriceCnyFen = Math.round(raw.originalPriceCNY * 100);
  if (raw.weight != null && raw.weight > 0) out.weightG = raw.weight;
  if (raw.length != null && raw.length > 0) out.lengthCm = raw.length;
  if (raw.width != null && raw.width > 0) out.widthCm = raw.width;
  if (raw.height != null && raw.height > 0) out.heightCm = raw.height;
  if (raw.checkMsg) out.checkMsg = raw.checkMsg;
  if (raw.createTime) out.createTime = raw.createTime;
  if (raw.storedTime) out.storedTime = raw.storedTime;
  if (raw.storedHouseName) out.storedHouseName = raw.storedHouseName;
  if (raw.expressOrderNo) out.expressOrderNo = raw.expressOrderNo;
  if (raw.serverTime != null) out.serverTime = raw.serverTime;
  if (raw.freeStorageDays != null) out.freeStorageDays = raw.freeStorageDays;
  if (raw.storageFreeTime != null) out.storageFreeTime = raw.storageFreeTime;
  if (raw.storageDiscardTime != null) out.storageDiscardTime = raw.storageDiscardTime;
  return out;
}

export async function queryStoredOrderItems(
  ctx: ApiFetchContext,
  authToken: string,
  input: StoredOrderListUrlInput = {},
  opts: OrderI18nOpts = {},
): Promise<StoredOrderListResult> {
  const page = await apiFetch<{
    records?: OrderItemRaw[];
    total?: number;
    size?: number;
    current?: number;
    pages?: number;
  }>(ctx, buildStoredOrderListUrl(input), { authToken, ...opts });
  const records = (page.records ?? []).map(normalizeStoredItem);
  return {
    records,
    total: page.total ?? records.length,
    pageNo: page.current ?? input.pageNo ?? 1,
    pageSize: page.size ?? input.pageSize ?? 10,
    pages: page.pages ?? 1,
  };
}

/**
 * `POST /express/create@2` (ExpressOrderCreateParamV2) — consolidate the picked
 * stored OrderItems into ONE international waybill bound for the given user
 * delivery address. Returns the new `expressOrderNo`. The backend re-validates
 * that every item is parcelable (STORED) and belongs to the caller; on failure
 * it returns an error envelope surfaced by `apiFetch` as an ApiError. The
 * customs/route-dependent billing weight is locked here, server-side.
 */
export async function createExpressOrderV2(
  ctx: ApiFetchContext,
  authToken: string,
  input: CreateExpressOrderInput,
): Promise<string> {
  return apiFetch<string>(ctx, "/express/create@2", {
    method: "POST",
    authToken,
    body: input,
  });
}

/**
 * `POST /order/storage/extend` (StorageExtendParam) — renew the free-storage
 * window for `quantity` units of an order by one paid 30-day block at the
 * sys_config `storageUnitPrice` (CNY/item). Returns the backend StorageOrder
 * envelope (kept loosely typed — the warehouse UI only needs success/failure).
 */
export async function storageExtend(
  ctx: ApiFetchContext,
  authToken: string,
  input: StorageExtendInput,
): Promise<unknown> {
  return apiFetch<unknown>(ctx, "/order/storage/extend", {
    method: "POST",
    authToken,
    body: input,
  });
}

/**
 * `POST /order/cancel` — self-service cancel of a whole order (#264).
 * Backend only honours this for INIT / WAIT_PAYMENT / WAIT_PROCESS; anything
 * later returns an error envelope and surfaces via `apiFetch` as an ApiError.
 * Returns the backend's status string (e.g. "Cancel order success!").
 */
export async function cancelOrder(
  ctx: ApiFetchContext,
  authToken: string,
  input: CancelOrderParam,
): Promise<string> {
  return apiFetch<string>(ctx, "/order/cancel", {
    method: "POST",
    authToken,
    body: input,
  });
}

/**
 * `POST /order/orderItemCancel` — cancel a single order item / sub-order (#264).
 * Same status gate as `cancelOrder`. `id` is the OrderItem ID.
 */
export async function cancelOrderItem(
  ctx: ApiFetchContext,
  authToken: string,
  input: CancelOrderItemParam,
): Promise<string> {
  return apiFetch<string>(ctx, "/order/orderItemCancel", {
    method: "POST",
    authToken,
    body: input,
  });
}
